import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { clientsApi } from "../../../services";
import type { Car, Client } from "../../../types";
import { AdminPageHeaderWithBack } from "../shared";
import {
  ActionButton,
  Avatar,
  Card,
  ConfirmationModal,
  EmptyState,
  ErrorState,
  Loading,
} from "../../shared";
import { Icons } from "../../shared/Icons";
import { CarsListView } from "./CarsListView";
import { CreateCarModal } from "./CreateCarModal";
import { displayPhoneNumber } from "../../../utils/phoneUtils";
import {
  getWhatsAppButtonStyles,
  getWhatsAppIconStyles,
  sendWhatsAppMessage,
  WHATSAPP_ICON_PATH,
} from "../../../utils/whatsappUtils";

export const AdminClientCars: React.FC = () => {
  const { clientId } = useParams<{
    clientId: string;
  }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCar, setEditingCar] = useState<Car | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [carToDelete, setCarToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const currentClientId = clientId ? parseInt(clientId, 10) : 0;

  const handleGoBack = () => {
    navigate(-1);
  };

  const fetchClientAndCars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const clientResponse = await clientsApi.getClientById(currentClientId);
      if (clientResponse.success && clientResponse.data) {
        setClient(clientResponse.data);
      } else {
        setError("Não foi possível carregar as informações do cliente");
        return;
      }

      const carsResponse = await clientsApi.getCarsByClient(currentClientId);
      if (carsResponse.success && carsResponse.data) {
        setCars(carsResponse.data);
      } else {
        setError(carsResponse.error || "Não foi possível carregar os veículos");
      }
    } catch (fetchError) {
      setError("Não foi possível carregar os dados");
      console.error("Error fetching client and cars:", fetchError);
    } finally {
      setLoading(false);
    }
  }, [currentClientId]);

  useEffect(() => {
    fetchClientAndCars();
  }, [fetchClientAndCars]);

  const handleCreateCar = () => {
    setEditingCar(null);
    setIsCreateModalOpen(true);
  };

  const handleEditCar = (car: Car) => {
    setEditingCar(car);
    setIsCreateModalOpen(true);
  };

  const handleDeleteCar = (carId: number) => {
    setCarToDelete(carId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!carToDelete) return;

    setDeleteLoading(true);
    setError(null);

    try {
      const response = await clientsApi.deleteCar(carToDelete);
      if (response.success) {
        await fetchClientAndCars();
        setShowDeleteModal(false);
        setCarToDelete(null);
      } else {
        setError(response.error || "Não foi possível excluir o veículo");
      }
    } catch (deleteError) {
      console.error("Error deleting car:", deleteError);
      setError("Não foi possível excluir o veículo");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCarToDelete(null);
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingCar(null);
    fetchClientAndCars();
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6 animate-fade-in">
        <AdminPageHeaderWithBack
          title="Veículos do Cliente"
          subtitle="Carregando..."
          icon={<Icons.Car sx={{ fontSize: 28 }} />}
          onBackClick={handleGoBack}
        />
        <Card className="p-4 sm:p-6">
          <Loading message="Carregando veículos..." />
        </Card>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="space-y-4 sm:space-y-6 animate-fade-in">
        <AdminPageHeaderWithBack
          title="Veículos do Cliente"
          subtitle="Erro ao carregar dados"
          icon={<Icons.Car sx={{ fontSize: 28 }} />}
          onBackClick={handleGoBack}
        />
        <ErrorState
          message={error}
          variant="card"
          onRetry={fetchClientAndCars}
        />
      </div>
    );
  }

  if (!client || !client.id) {
    return (
      <div className="space-y-4 sm:space-y-6 animate-fade-in">
        <AdminPageHeaderWithBack
          title="Veículos do Cliente"
          subtitle="Cliente não encontrado"
          icon={<Icons.Car sx={{ fontSize: 28 }} />}
          onBackClick={handleGoBack}
        />
        <ErrorState
          title="Cliente não encontrado"
          message="Não foi possível localizar este cliente."
          variant="card"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <AdminPageHeaderWithBack
        title={`Veículos de ${client.name}`}
        subtitle={`${cars.length} veículo${cars.length !== 1 ? "s" : ""} cadastrado${
          cars.length !== 1 ? "s" : ""
        }`}
        icon={<Icons.Car sx={{ fontSize: 28 }} />}
        onBackClick={handleGoBack}
      />

      <Card className="p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Avatar text={client.name} size="lg" variant="blue" />
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => navigate(`/admin/clients/${client.id}`)}
                className="text-lg sm:text-xl font-bold text-gray-900 hover:text-blue-700 transition-colors text-left break-words"
              >
                {client.name}
              </button>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 min-w-0 text-gray-600">
                  <Icons.Phone sx={{ fontSize: 16, color: "#3b82f6" }} />
                  <span className="text-sm break-all">
                    {client.phoneNumber
                      ? displayPhoneNumber(client.phoneNumber)
                      : "Sem telefone"}
                  </span>
                </div>
                {client.phoneNumber ? (
                  <button
                    type="button"
                    onClick={() =>
                      sendWhatsAppMessage({
                        phoneNumber: client.phoneNumber,
                        message: "",
                      })
                    }
                    className={getWhatsAppButtonStyles("sm")}
                    title="Enviar mensagem via WhatsApp"
                  >
                    <svg
                      className={getWhatsAppIconStyles("sm")}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={WHATSAPP_ICON_PATH} />
                    </svg>
                  </button>
                ) : null}
              </div>
              {client.address ? (
                <p className="mt-1.5 text-sm text-gray-500 break-words">
                  📍 {client.address}
                </p>
              ) : null}
            </div>
          </div>
          <ActionButton
            onClick={handleCreateCar}
            variant="primary"
            size="sm"
            icon={<Icons.Add sx={{ fontSize: 18 }} />}
            className="w-full sm:w-auto justify-center shrink-0"
          >
            Adicionar veículo
          </ActionButton>
        </div>
      </Card>

      {error ? (
        <ErrorState
          message={error}
          variant="banner"
          size="sm"
          onRetry={fetchClientAndCars}
        />
      ) : null}

      {cars.length === 0 ? (
        <Card className="p-4 sm:p-6">
          <EmptyState
            icon={<Icons.Car sx={{ fontSize: 80 }} />}
            title="Nenhum veículo cadastrado"
            description="Comece adicionando o primeiro veículo deste cliente."
            actionText="Adicionar primeiro veículo"
            onAction={handleCreateCar}
          />
        </Card>
      ) : (
        <CarsListView
          cars={cars}
          onEdit={handleEditCar}
          onDelete={handleDeleteCar}
        />
      )}

      <CreateCarModal
        isOpen={isCreateModalOpen}
        onClose={handleModalClose}
        car={editingCar}
        clientId={currentClientId}
        clientName={client.name}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Veículo"
        message="Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
};
